/**
 * Tab-scoped record that the seller's iPad is in the customer's hands.
 *
 * `sessionStorage` does not fire a storage event in the tab that wrote it.
 * Subscribers are told in the same turn as the write, so the root gate can
 * mount without a reload. Removing the key any other way leaves subscribers
 * where they are: the screen stays up until `clearHandoff`.
 */

const STORAGE_KEY = "joshua.presencial.handoff";

export interface PresencialHandoff {
  sessaoId: string;
  signatarioIndice: number;
  nome: string;
}

type Listener = (record: PresencialHandoff | null) => void;

const listeners = new Set<Listener>();

function isRecord(value: unknown): value is PresencialHandoff {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.sessaoId === "string" &&
    row.sessaoId.length > 0 &&
    typeof row.signatarioIndice === "number" &&
    Number.isInteger(row.signatarioIndice) &&
    row.signatarioIndice >= 0 &&
    typeof row.nome === "string" &&
    row.nome.trim().length > 0;
}

/** Read the record. A missing or unreadable value is no handoff. */
export function readHandoff(): PresencialHandoff | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    return {
      sessaoId: parsed.sessaoId,
      signatarioIndice: parsed.signatarioIndice,
      nome: parsed.nome.trim(),
    };
  } catch {
    return null;
  }
}

function notify(record: PresencialHandoff | null): void {
  for (const listener of listeners) listener(record);
}

/** Store the handoff and tell same-tab subscribers before returning. */
export function writeHandoff(record: PresencialHandoff): void {
  const stored: PresencialHandoff = {
    sessaoId: record.sessaoId,
    signatarioIndice: record.signatarioIndice,
    nome: record.nome.trim(),
  };
  if (!isRecord(stored)) return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  notify(stored);
}

/** Drop the record and tell same-tab subscribers before returning. */
export function clearHandoff(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  notify(null);
}

/** Subscribe to writes from this tab. The listener is not called for the current value. */
export function subscribeHandoff(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Canvas stroke key for this handoff. A reload restores the drawing with the record. */
export function handoffPersistKey(record: PresencialHandoff): string {
  return `presencial:${record.sessaoId}:${record.signatarioIndice}`;
}
